/**
 * Cross-tab WL session sync (CapHQ parity).
 * Token stays in sessionStorage (dies when all tabs close).
 * BroadcastChannel + ephemeral localStorage mailbox share it while any tab is open.
 */

export type AuthSyncMessage =
  | { type: 'TOKEN_REQUEST' }
  | { type: 'TOKEN_OFFER'; token: string }
  | { type: 'LOGOUT' }
  | { type: 'ACTIVITY' };

const CHANNEL_NAME = 'wl-auth-sync';
/** Ephemeral mailbox only — never leave the token here. */
const MAILBOX_KEY = 'wl.authSyncMailbox';
/** Max wait for a peer to offer a token on a new empty tab. */
export const AUTH_SYNC_WAIT_MS = 250;
const ACTIVITY_THROTTLE_MS = 5_000;

type Handler = (message: AuthSyncMessage) => void;

const handlers = new Set<Handler>();
let channel: BroadcastChannel | null = null;
let storageListening = false;
let lastActivityBroadcastAt = 0;

function canUseStorage() {
  try {
    const k = '__wl_auth_sync_probe__';
    localStorage.setItem(k, '1');
    localStorage.removeItem(k);
    return true;
  } catch {
    return false;
  }
}

function notify(message: AuthSyncMessage) {
  for (const handler of handlers) {
    try {
      handler(message);
    } catch {
      // ignore subscriber errors
    }
  }
}

function parseMessage(raw: unknown): AuthSyncMessage | null {
  if (!raw || typeof raw !== 'object') return null;
  const msg = raw as AuthSyncMessage;
  if (msg.type === 'TOKEN_REQUEST') return { type: 'TOKEN_REQUEST' };
  if (msg.type === 'LOGOUT') return { type: 'LOGOUT' };
  if (msg.type === 'ACTIVITY') return { type: 'ACTIVITY' };
  if (
    msg.type === 'TOKEN_OFFER' &&
    typeof msg.token === 'string' &&
    msg.token
  ) {
    return { type: 'TOKEN_OFFER', token: msg.token };
  }
  return null;
}

function ensureChannel() {
  if (typeof window === 'undefined') return;
  if (channel || typeof BroadcastChannel === 'undefined') return;

  channel = new BroadcastChannel(CHANNEL_NAME);
  channel.onmessage = (event) => {
    const message = parseMessage(event.data);
    if (message) notify(message);
  };
}

function ensureStorageFallback() {
  if (typeof window === 'undefined' || storageListening || !canUseStorage()) {
    return;
  }

  storageListening = true;
  window.addEventListener('storage', (event) => {
    if (event.key !== MAILBOX_KEY || !event.newValue) return;
    try {
      const message = parseMessage(JSON.parse(event.newValue));
      if (message) notify(message);
    } catch {
      // ignore malformed mailbox payloads
    }
  });
}

/** Subscribe to cross-tab auth messages. Returns unsubscribe. */
export function subscribeAuthSync(handler: Handler): () => void {
  ensureChannel();
  ensureStorageFallback();
  handlers.add(handler);
  return () => {
    handlers.delete(handler);
  };
}

export function publishAuthSync(message: AuthSyncMessage) {
  ensureChannel();
  ensureStorageFallback();

  channel?.postMessage(message);

  if (!canUseStorage()) return;
  try {
    const payload = JSON.stringify({ ...message, _ts: Date.now() });
    localStorage.setItem(MAILBOX_KEY, payload);
    localStorage.removeItem(MAILBOX_KEY);
  } catch {
    // private mode / blocked storage
  }
}

export function publishTokenOffer(token: string) {
  if (!token) return;
  publishAuthSync({ type: 'TOKEN_OFFER', token });
}

export function publishLogout() {
  publishAuthSync({ type: 'LOGOUT' });
}

/** Throttled so pointer spam does not flood other tabs. */
export function publishActivityThrottled() {
  const now = Date.now();
  if (now - lastActivityBroadcastAt < ACTIVITY_THROTTLE_MS) return;
  lastActivityBroadcastAt = now;
  publishAuthSync({ type: 'ACTIVITY' });
}

/**
 * Ask open WL tabs for a session token.
 * Resolves with the first offer, or null after {@link AUTH_SYNC_WAIT_MS}.
 */
export function requestTokenFromPeers(
  waitMs: number = AUTH_SYNC_WAIT_MS,
): Promise<string | null> {
  if (typeof window === 'undefined') return Promise.resolve(null);

  return new Promise((resolve) => {
    let settled = false;
    const finish = (token: string | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      unsubscribe();
      resolve(token);
    };

    const unsubscribe = subscribeAuthSync((message) => {
      if (message.type === 'TOKEN_OFFER') {
        finish(message.token);
      }
    });

    const timer = window.setTimeout(() => finish(null), waitMs);
    publishAuthSync({ type: 'TOKEN_REQUEST' });
  });
}
